"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronDown, ArrowRight, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
interface FaqItem {
  id: string;
  question: string;
  answer: string;
}
const faqItems: FaqItem[] = [{
  id: "item-1",
  question: "How do Brookhaven zoning setbacks, impervious surface rules, and tree canopy laws affect additions?",
  answer: "Brookhaven enforces rigorous zoning setback lines and maximum lot impervious surface ratios—typically capped between 35% and 50% depending on your specific district (such as R-100 or R-75). In addition, the City of Brookhaven Tree Canopy Ordinance mandates tree protection zones and tree density unit calculations before groundbreaking. Our team handles complete boundary surveys, impervious footprint calculations, tree mitigation plans, and city approvals before construction begins."
}, {
  id: "item-2",
  question: "How do you manage subgrade moisture, vapor barriers, and interior humidity in Georgia basements?",
  answer: "Georgia red clay soil holds heavy moisture during seasonal storms. We install reinforced sub-slab vapor barriers with sealed joints, closed-cell spray foam insulation along rim joists, and continuous interior or exterior perimeter drainage channels with dual battery-backed sump systems. Every finished basement also integrates dedicated whole-home dehumidification tied directly into your HVAC ductwork to maintain indoor humidity below 50% year-round."
}, {
  id: "item-3",
  question: "Composite decking vs. pressure-treated wood: which holds up better to Atlanta heat and humidity?",
  answer: "Pressure-treated pine requires sealing and staining every 12 to 18 months and remains prone to cupping, splintering, and mildew under hot Southern humidity. Premium capped composite and mineral-based composite boards resist moisture absorption and insect damage, never require sanding, and carry 25- to 50-year fade and stain warranties. For unshaded, south-facing exposures, we specify heat-dissipating composite profiles that remain cooler underfoot."
}, {
  id: "item-4",
  question: "What structural discovery steps are required for second-story additions and rear bump-outs?",
  answer: "Before drafting architectural blueprints, our builders perform foundation sounding tests, inspect existing footings, and verify load paths through exterior wall studs and floor joists. For second-story builds, we inspect crawlspaces and basements to confirm foundation widths can support supplementary point loads, designing steel beam integrations or reinforced post footings wherever the existing frame requires fortification."
}];
export default function Questions() {
  const [openId, setOpenId] = useState<string>("item-1");
  const shouldReduceMotion = useReducedMotion();
  const toggleAccordion = (id: string) => {
    setOpenId(prev => prev === id ? "" : id);
  };
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.01 : 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="frequently-asked-questions" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="container mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column (5 Cols) */}
          <div className="lg:col-span-5 flex flex-col items-start space-y-6">
            <Badge variant="outline" className="bg-primary/10 border-primary/30 text-primary-foreground font-sans uppercase tracking-widest text-xs px-3 py-1 font-semibold">
              Planning & Compliance
            </Badge>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold tracking-tight text-foreground leading-tight">
              Answers to key planning and structural questions
            </h2>

            <div className="w-16 h-[2px] bg-primary/60" />

            <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
              Every major addition, walkout basement, or covered outdoor living space in Brookhaven involves strict city setback rules, structural engineering checks, and water management. Here is how we ensure certainty before we break ground.
            </p>

            {/* Direct Consultation Prompt Card */}
            <div className="w-full rounded-xl bg-card border border-border/80 p-6 shadow-sm space-y-4">
              <div className="flex items-start gap-3">
                <div className="rounded-full bg-primary/10 p-2.5 text-primary shrink-0">
                  <Phone className="size-4" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground font-sans">
                    Have a specific site question?
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1 font-sans">
                    Speak directly with Carlos Ramirez or Emily Johnson about your property boundaries and architectural vision.
                  </p>
                </div>
              </div>

              <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <Button asChild variant="default" className="font-sans font-semibold">
                  <Link to="/ContactRequestAnEstimatePage">
                    Schedule a Consultation
                    <ArrowRight className="size-4 ml-1" />
                  </Link>
                </Button>
                <a href="tel:+14708479076" className="inline-flex items-center justify-center text-sm font-medium text-foreground hover:text-primary transition-colors py-2 px-3">
                  (470) 847-9076
                </a>
              </div>
            </div>
          </div>

          {/* Right Column (7 Cols): Atelier Card with Accordions */}
          <div className="lg:col-span-7">
            <div className="rounded-2xl border border-white/80 bg-white/75 backdrop-blur-md shadow-sm p-6 sm:p-8 transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-md">
              <div className="divide-y divide-border/70">
                {faqItems.map((item, index) => {
                const isOpen = openId === item.id;
                return <div key={item.id} data-index={index} className="py-5 first:pt-0 last:pb-0">
                      <Button variant="ghost" type="button" onClick={() => toggleAccordion(item.id)} aria-expanded={isOpen} aria-controls={`faq-answer-${item.id}`} className="h-auto p-0 w-full flex items-start justify-between gap-4 text-left group cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-md hover:bg-transparent whitespace-normal">
                        <span className="text-base sm:text-lg font-semibold font-sans text-foreground group-hover:text-primary transition-colors leading-snug">
                          {item.question}
                        </span>
                        <span className="shrink-0 mt-1 rounded-full p-1 bg-muted/60 text-muted-foreground group-hover:text-primary group-hover:bg-primary/10 transition-colors">
                          <ChevronDown className={`size-4 transition-transform duration-300 ease-out ${isOpen ? "rotate-180 text-primary" : ""}`} />
                        </span>
                      </Button>

                      <div id={`faq-answer-${item.id}`} className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                        <div className="overflow-hidden">
                          <p className="pt-3 pr-4 sm:pr-8 text-sm sm:text-base text-muted-foreground font-sans leading-relaxed">
                            {item.answer}
                          </p>
                        </div>
                      </div>
                    </div>;
              })}
              </div>

              {/* Card Footer Footnote */}
              <div className="mt-8 pt-6 border-t border-border/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-muted-foreground font-sans">
                <span>All construction permits pulled through Brookhaven Community Development.</span>
                <Link to="/AboutPage" className="font-medium text-foreground hover:text-primary transition-colors inline-flex items-center gap-1">Explore our 4-step process
                  <ArrowRight className="size-3" /></Link>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}